import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Shield, Eye, EyeOff, Loader, Check, Lock, Mail, Building2, AlertTriangle } from "lucide-react";
import { useAuth, formatApiError } from "../contexts/AuthContext";
import { useWorkspaceBranding, cachePublicBranding } from "../lib/useWorkspaceBranding";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function AcceptInvitePage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [preview, setPreview] = useState(null);
  const branding = useWorkspaceBranding(preview?.workspace_slug);
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [initializing, setInitializing] = useState(true);

  useEffect(() => {
    if (!token) { setError("Kein Einladungs-Token"); setInitializing(false); return; }
    axios.get(`${API}/auth/invite/preview?token=${encodeURIComponent(token)}`)
      .then(r => { setPreview(r.data); setInitializing(false); })
      .catch(err => { setError(formatApiError(err.response?.data?.detail) || "Ungültige Einladung"); setInitializing(false); });
  }, [token]);

  const checks = {
    length: pw.length >= 10,
    upper: /[A-Z]/.test(pw),
    lower: /[a-z]/.test(pw),
    digit: /\d/.test(pw),
    special: /[^A-Za-z0-9]/.test(pw),
    match: pw && pw === pw2,
  };
  const allOk = Object.values(checks).every(Boolean);

  const submit = async (e) => {
    e.preventDefault();
    if (!allOk) { setError("Passwort erfüllt noch nicht alle Anforderungen"); return; }
    setError(""); setLoading(true);
    try {
      const r = await axios.post(`${API}/auth/invite/accept`, { token, password: pw }, { withCredentials: true });
      setUser(r.data);
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Aktivierung fehlgeschlagen");
    } finally { setLoading(false); }
  };

  if (initializing) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader size={24} className="animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!preview) {
    return (
      <div className="min-h-screen flex items-center justify-center relative overflow-hidden bg-[#0a0e1a] p-4">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_30%_20%,hsl(var(--primary)/0.55),transparent_50%),radial-gradient(ellipse_at_70%_60%,hsl(var(--primary)/0.35),transparent_55%),radial-gradient(ellipse_at_50%_90%,#1d4ed8_0%,transparent_45%)]" />
        <div className="absolute inset-0 login-aurora-a" />
        <div className="absolute inset-0 login-aurora-b" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/50" />
        <div className="relative z-10 w-full max-w-md bg-card border border-border shadow-xl p-8 text-center">
          <AlertTriangle size={40} className="mx-auto mb-4 text-destructive" />
          <h1 className="text-xl font-bold mb-2">Einladung ungültig</h1>
          <p className="text-sm text-muted-foreground mb-6">{error || "Der Einladungs-Link ist abgelaufen oder wurde bereits verwendet."}</p>
          <Link to="/login" className="inline-block px-5 py-2.5 bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90">
            Zum Login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden bg-[#0a0e1a] p-4">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_30%_20%,hsl(var(--primary)/0.55),transparent_50%),radial-gradient(ellipse_at_70%_60%,hsl(var(--primary)/0.35),transparent_55%),radial-gradient(ellipse_at_50%_90%,#1d4ed8_0%,transparent_45%)]" />
      <div className="absolute inset-0 login-aurora-a" />
      <div className="absolute inset-0 login-aurora-b" />
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/50" />
      <div className="relative z-10 w-full max-w-md animate-in">
        <div className="flex items-center gap-3 mb-6">
          {branding?.logo_data_uri ? (
            <img src={branding.logo_data_uri} alt="Logo" className="w-10 h-10 object-contain" data-testid="invite-branded-logo" />
          ) : (
            <div className="w-10 h-10 bg-primary text-primary-foreground rounded-lg flex items-center justify-center">
              <Shield size={20} />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <h2 className="text-2xl font-bold tracking-tight truncate" data-testid="invite-branded-name">{branding?.display_name || "DATON"}</h2>
            <p className="text-xs text-muted-foreground">Account aktivieren{branding?.display_name ? " · powered by DATON" : ""}</p>
          </div>
        </div>

        <div className="bg-card border border-border shadow-soft p-6" data-testid="accept-invite-card">
          <h1 className="text-xl font-semibold mb-1">Willkommen, {preview.name} 👋</h1>
          <p className="text-sm text-muted-foreground mb-5">
            Du wurdest zum Workspace <strong className="text-primary">{preview.workspace_name}</strong> eingeladen. Setze jetzt dein Passwort.
          </p>

          <div className="bg-muted/30 border border-border px-3 py-2.5 mb-4 flex items-center gap-2.5 text-sm">
            <Mail size={14} className="text-muted-foreground flex-shrink-0" />
            <span className="font-mono text-muted-foreground truncate">{preview.email}</span>
          </div>
          <div className="bg-muted/30 border border-border px-3 py-2.5 mb-4 flex items-center gap-2.5 text-sm">
            <Building2 size={14} className="text-muted-foreground flex-shrink-0" />
            <span className="truncate"><strong>{preview.workspace_name}</strong></span>
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-xs uppercase tracking-widest font-bold text-muted-foreground block mb-1.5 flex items-center gap-1.5">
                <Lock size={11} /> Neues Passwort
              </label>
              <div className="relative">
                <input data-testid="accept-password" type={showPw ? "text" : "password"} value={pw} onChange={e => setPw(e.target.value)} required autoFocus
                  className="w-full px-3 py-2.5 pr-10 text-sm bg-background border border-input focus:border-primary focus:outline-none"
                  placeholder="Mindestens 10 Zeichen" />
                <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                  {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest font-bold text-muted-foreground block mb-1.5">Passwort wiederholen</label>
              <input data-testid="accept-password-repeat" type={showPw ? "text" : "password"} value={pw2} onChange={e => setPw2(e.target.value)} required
                className="w-full px-3 py-2.5 text-sm bg-background border border-input focus:border-primary focus:outline-none" />
            </div>

            {pw && (
              <div className="grid grid-cols-2 gap-1 text-[11px]">
                <Chk label="≥ 10 Zeichen" ok={checks.length} />
                <Chk label="Grossbuchstabe" ok={checks.upper} />
                <Chk label="Kleinbuchstabe" ok={checks.lower} />
                <Chk label="Zahl" ok={checks.digit} />
                <Chk label="Sonderzeichen" ok={checks.special} />
                <Chk label="Passwörter stimmen überein" ok={checks.match} />
              </div>
            )}

            {error && <div data-testid="accept-error" className="text-sm text-destructive bg-destructive/10 px-3 py-2 border border-destructive/20">{error}</div>}

            <button data-testid="accept-submit" type="submit" disabled={loading || !allOk}
              className="w-full py-2.5 bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 disabled:opacity-50 transition-colors flex items-center justify-center gap-2">
              {loading && <Loader size={15} className="animate-spin" />}
              {loading ? "Wird aktiviert..." : "Account aktivieren & einloggen"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

function Chk({ label, ok }) {
  return (
    <div className={`flex items-center gap-1 ${ok ? "text-emerald-600 dark:text-emerald-400" : "text-muted-foreground/60"}`}>
      <Check size={9} className={ok ? "" : "opacity-30"} /> {label}
    </div>
  );
}
